<template>
  <v-container
    class="rounded-lg elevation-3 d-flex flex-column px-4 pb-4"
    style="height: 100%; background-color: var(--color-bg)"
    fluid
  >
    <div class="d-flex justify-space-between align-center">
      <h3
        class="font-weight-light"
        style="color: var(--color-text)"
      >
        Speed Consumption Table
      </h3>

      <v-spacer />


      <!-- Excel export button -->
      <v-btn
        small
        height="28"
        class="mx-1 px-2"
        color="#439346"
        dark
        elevation="0"
        outlined
        @click="exportTableToExcel"
      >
        <v-icon
          left
          small
        >
          mdi-file-excel
        </v-icon>
        Export Excel
      </v-btn>

      <ThemedCheckbox
        v-model="showConsumption"
        class="pa-0 ma-0"
        dense
        :label="auxiliaryConsumptionLabel"
        :input-value="showConsumption"
      />
    </div>

    <div style="height: 1em" />

    <v-container
      style="height: 100%;
      overflow-y: scroll !important ;
      overflow-x: auto;"
      class="pa-0"
    >
      <v-data-table
        :headers="tableHeaders"
        :items="tableItems"
        disable-pagination
        height="30vh"
        hide-default-footer
        hide-default-header
      >
        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th
                v-for="header in headers"
                :key="header.text"
                :class="header.class"
                :style="{ width: header.width, textAlign: header.align }"
              >
                <v-tooltip
                  v-if="header.tooltip"
                  bottom
                >
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                    >{{ header.text }}</span>
                  </template>
                  <span>{{ header.tooltip }}</span>
                </v-tooltip>
                <span v-else>{{ header.text }}</span>
              </th>
            </tr>
          </thead>
        </template>
      </v-data-table>
    </v-container>
  </v-container>
</template>

<script>
import * as XLSX from "xlsx";
import { mapState, mapGetters } from "vuex";
import ThemedCheckbox from "@/core/ThemedCheckbox.vue";

export default {
  name: "ResultsTable",
  components: {
    ThemedCheckbox,
  },
  data() {
    return {
      showConsumption: true,
    };
  },
  computed: {
    ...mapState("kDiagram", ["calculationResult", "calculationData"]),
    ...mapGetters("kDiagram", [
      "speedsOfInterest",
      "powerOfInterest",
      "consumptionOfInterest",
      "rpmOfInterest",
      "isoDailyConsumptionOfInterest",
      "auxiliaryConsumption",
    ]),

    tableItems() {
      if (!this.calculationResult) return [];

      return this.rpmOfInterest.map((rpm, index) => {
        return {
          rpm : Number(rpm).toFixed(1),
          speed: this.speedsOfInterest[index],
          power: Number(this.powerOfInterest[index]).toFixed(1),
          isoDailyConsumption: Number(this.isoDailyConsumptionOfInterest[index]).toFixed(1),
          total: (
            this.consumptionOfInterest[index] +
            (this.showConsumption ? this.auxiliaryConsumption : 0)
          ).toFixed(1),
        };
      });
    },

    tableHeaders() {
      return [
        { text: "Speed (kn)", value: "speed", align: "center", width: "20px" },
        { text: "RPM", value: "rpm", align: "center", width: "20px" },
        { text: "Power (kW)", value: "power", align: "center", width: "20px" },
        {
          text: "ISO-Cons. (tn/day)",
          value: "isoDailyConsumption",
          align: "center",
          tooltip: "ISO standard daily fuel consumption in tonnes",
        },
        {
          text: "Actual FOC (tn/day)",
          value: "total",
          align: "center",
          tooltip: "Total fuel consumption including auxiliary consumption",
        },

      ];
    },

    auxiliaryConsumptionLabel() {
      return `Include aux. cons. of ${this.auxiliaryConsumption} (tn/day)`;
    },
  },

  methods: {
exportTableToExcel() {
  if (!this.tableItems.length || !this.calculationData) return;

  const vessel =
    this.$route && this.$route.params && this.$route.params.vessel
      ? this.$route.params.vessel
      : "Vessel";

  const now = new Date();
  const exportTime =
    now.getUTCFullYear() +
    "-" +
    String(now.getUTCMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getUTCDate()).padStart(2, "0") +
    " " +
    String(now.getUTCHours()).padStart(2, "0") +
    ":" +
    String(now.getUTCMinutes()).padStart(2, "0") +
    " UTC";

  // ---- Inputs ----
  const meanDraft = this.calculationData.meanDraft;
  const trim = this.calculationData.trim;
  const windBft = this.calculationData.trueWindSpeed;
  const fouling = this.calculationData.fouling;
  const lhv = this.calculationData.lowerHeatingValue;

  // Wind direction mapping
  const windDirRaw = this.calculationData.windDirection;
  let windDirText = "";
  if (windDirRaw === 0) windDirText = "Head seas";
  else if (windDirRaw === 1) windDirText = "Beam seas";
  else if (windDirRaw === 2) windDirText = "Following seas";
  else windDirText = String(windDirRaw);

  // ---- Build Excel ----
  const aoa = [];

  // Metadata (✔ corrected explanation)
  aoa.push(["Vessel", vessel]);
  aoa.push(["Exported at", exportTime]);
  aoa.push([
    "ME Actual FOC is based on the measurered consumption of the vessel, " +
    "with the fuel used onboard over the last month. ",
  ]);
  aoa.push(["Aux. consumption is the average consumption over the last month"]);
  aoa.push(["Auxiliary consumption (tn/day)", Number(this.auxiliaryConsumption).toFixed(2)]);

  // Header row
  aoa.push([
    "Speed (kn)",
    "RPM",
    "Power (kW)",
    "ME ISO Cons. (tn/day)",
    "ME Actual FOC (tn/day)",
    "Total Actual FOC incl. Aux (tn/day)",
    "Mean Draft (m)",
    "Trim (m)",
    "Wind (BFT)",
    "Wind Direction",
    "Fouling",
    "LHV (kJ/kg)",
  ]);


  this.tableItems.forEach((row, index) => {
    const iso = Number(row.isoDailyConsumption);
    const actualExclAux = Number(this.consumptionOfInterest[index]);
    const aux = Number(this.auxiliaryConsumption);

    aoa.push([
  Number(row.speed).toFixed(1),
  Number(row.rpm).toFixed(1),
  Number(row.power).toFixed(0),
  iso.toFixed(1),
  actualExclAux.toFixed(1),
  (actualExclAux + aux).toFixed(1),
  meanDraft,
  trim,
  windBft,
  windDirText,
  fouling,
  lhv,
]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!cols"] = [
    { wch: 23 },
    { wch: 18 },
    { wch: 14 },
    { wch: 17 },
    { wch: 20 },
    { wch: 28 },
    { wch: 13 },
    { wch: 9 },
    { wch: 11 },
    { wch: 15 },
    { wch: 8 },
    { wch: 12 },
  ];

  ws["!freeze"] = { ySplit: 5 };

  const boldStyle = { font: { bold: true } };
  ws["A1"].s = boldStyle;
  ws["A2"].s = boldStyle;
  ws["A3"].s = boldStyle;

  const headerRowIndex = 4;
  for (let c = 0; c < 12; c++) {
    const cellAddr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    if (ws[cellAddr]) ws[cellAddr].s = boldStyle;
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data Table");

  const filename = `QUAD_${vessel}_DataTable.xls`;
  const wbout = XLSX.write(wb, { bookType: "xls", type: "array" });

  const blob = new Blob([wbout], {
    type: "application/vnd.ms-excel",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
},
  },
};
</script>

<style scoped>
/* Container scrolling - always show vertical scrollbar */
.v-container {
  overflow-y: scroll !important;
  overflow-x: auto !important;
  scrollbar-gutter: stable;
}

/* Data table styling */
.v-data-table {
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
}

.v-data-table >>> th {
  background-color: var(--color-bg) !important;
  color: var(--color-text) !important;
  border-bottom: 1px solid var(--color-border) !important;
}

.v-data-table >>> td {
  border-bottom: 1px solid var(--color-border) !important;
  color: var(--color-text) !important;
}

.v-data-table >>> tbody tr:hover {
  background-color: var(--color-overlay) !important;
}

.v-data-table >>> tbody tr:nth-of-type(odd) {
  background-color: var(--color-surface) !important;
}

.v-data-table >>> tbody tr:nth-of-type(even) {
  background-color: var(--color-bg) !important;
}

/* Column spacing */
.v-data-table >>> th,
.v-data-table >>> td {
  padding-left: 70px !important;
  padding-right: 70px !important;
}
</style>
